import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../utils/constants";
import ProgressBar from "../../components/ProgressBar";
import { readErrorMessage } from "../../utils/apiError";
import { buildCases, toExcelBlob } from "./lotUpload";

// Server messages put in plain words for the error list
const plainServerMessage = (message) => {
  if (/Duplication of Refrence Number/i.test(message)) {
    return "This Reference No is already uploaded (in this or an earlier lot). Remove this case from the Excel, or correct its Reference No.";
  }
  if (/^[A-Za-z_.]+: /.test(message)) {
    return `Some details of this case are not in the right form (${message}). Check this case's row(s) against the LOT template.`;
  }
  return message;
};

// Verify + Upload: one record per case, built from the product's column setup
const LotVerifyUpload = ({
  config,
  excelData,
  bankId,
  selectedProductID,
  verified,
  setVerified,
  setShowProgress,
  setProg,
  setTotalRecords,
  setClearForm,
  clearForm,
  showProgress,
  setActiveStep,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorResponses, setErrorResponses] = useState([]);
  const [verifyProgress, setVerifyProgress] = useState(0);
  const [uploadSummary, setUploadSummary] = useState(null);

  const { cases, errors: groupingErrors } = useMemo(
    () => buildCases(excelData, config, Number(bankId), Number(selectedProductID)),
    [excelData, config, bankId, selectedProductID],
  );

  const toErrorRow = ({ rowNo, refNo, record }, message) => ({
    RowNo: rowNo,
    Reference_no: refNo,
    Acc_no: record.Acc_no || "",
    Cust_name: record.Borrower?.[0]?.Cust_name || "",
    ErrorMessage: message,
  });

  const post = async (path, record) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(record),
      });
      return res.ok ? null : plainServerMessage(await readErrorMessage(res));
    } catch (e) {
      return e.message;
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setVerifyProgress(0);
    const errors = [...groupingErrors];

    for (const item of cases) {
      const message = await post("Validate", item.record);
      if (message) errors.push(toErrorRow(item, message));
      setVerifyProgress((p) => p + 1);
    }

    setErrorResponses(errors);
    setVerified(errors.length === 0);
    if (errors.length === 0) setActiveStep(2);
    setLoading(false);
  };

  const handleUpload = async () => {
    setShowProgress(true);
    setProg(0);
    setTotalRecords(cases.length);
    const failures = [];

    for (const item of cases) {
      const message = await post("UploadData", item.record);
      if (message) failures.push(toErrorRow(item, message));
      setProg((p) => p + 1);
    }

    if (failures.length === 0) {
      setClearForm(true);
    } else {
      setShowProgress(false);
      setErrorResponses(failures);
      setUploadSummary({ uploaded: cases.length - failures.length, failed: failures.length });
    }
  };

  const downloadErrorExcel = () => {
    const url = URL.createObjectURL(toExcelBlob(XLSX, errorResponses));
    const a = document.createElement("a");
    a.href = url;
    a.download = "ErrorReport.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-3">
      {uploadSummary && (
        <p className="text-danger fw-bold text-end" style={{ fontSize: "12px" }}>
          Uploaded {uploadSummary.uploaded} case(s), {uploadSummary.failed} failed. Download the error Excel for
          details.
        </p>
      )}
      {!loading && (
        <div className="d-flex justify-content-end gap-2">
          {excelData.length > 0 && !verified && errorResponses.length === 0 && (
            <button className="custBtn" style={{ fontSize: "12px" }} onClick={handleVerify}>
              Verify ({cases.length} cases)
            </button>
          )}
          {errorResponses.length > 0 && (
            <button className="custBtn" style={{ fontSize: "12px" }} onClick={downloadErrorExcel}>
              Download Error Excel ({errorResponses.length})
            </button>
          )}
          {verified && !clearForm && !showProgress && !uploadSummary && errorResponses.length === 0 && (
            <button className="custBtn" style={{ fontSize: "12px" }} onClick={handleUpload}>
              Upload
            </button>
          )}
        </div>
      )}

      {loading && (
        <ProgressBar
          progress={verifyProgress}
          total={cases.length}
          show
          label={`Verified ${verifyProgress} of ${cases.length}`}
        />
      )}
    </div>
  );
};

export default LotVerifyUpload;
